import React, { useEffect, useState } from "react";

/**
 * Cinematic vertical teaser optimised for 9:16+ screen-recording.
 *
 * Structure (50s loop, 5 pages):
 *   Page 0 — Versets (Bismillah ▸ verset ▸ traduction)    0   → 6.5 s
 *   Page A — Monogramme intro (Y & M drawn)               7   → 21  s
 *   Page B — Couple + Pagnes                              21.5 → 36  s
 *   Page C — Date banner                                  36.5 → 42  s
 *   Page D — CTA                                          42.5 → 49.5 s
 */
export default function VideoTeaser() {
  const [restart, setRestart] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setRestart((r) => r + 1), 50000);
    return () => clearInterval(id);
  }, []);

  return (
    <div key={restart} className="teaser-root" data-testid="video-teaser">
      <div className="teaser-bg" />
      <div className="teaser-vignette" />

      {/* Gold sparkle layer — bursts at every page transition */}
      <div className="sparkles" aria-hidden="true">
        <span className="sparkle sp-a" />
        <span className="sparkle sp-b" />
        <span className="sparkle sp-c" />
        <span className="sparkle sp-d" />
        <span className="sparkle sp-e" />
        <span className="sparkle sp-f" />
        <span className="sparkle sp-g" />
        <span className="sparkle sp-h" />
        <span className="sparkle sp-i" />
        <span className="sparkle sp-j" />
        <span className="sparkle sp-k" />
        <span className="sparkle sp-l" />
      </div>

      {/* ============= Page 0 — Versets ============= */}
      <div className="teaser-page page-intro">
        <div className="reveal reveal-1">
          <p className="bismillah" dir="rtl">
            بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيمِ
          </p>
          <span className="gold-rule" />
        </div>

        <div className="reveal reveal-2">
          <p className="verse" dir="rtl">
            وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُمْ مِنْ أَنْفُسِكُمْ أَزْوَاجًا
            <br />
            لِتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُمْ مَوَدَّةً وَرَحْمَةً
          </p>
          <p className="verse-ref">٣٠ : ٢١ — سُورَةُ الرُّومِ</p>
        </div>

        <div className="reveal reveal-3">
          <p className="translation">
            « Il a créé pour vous, des vôtres,
            <br />
            des épouses afin que vous trouviez tranquillité,
            <br />
            et Il a mis entre vous affection et bonté. »
          </p>
        </div>
      </div>

      {/* ============= Page A — Monogramme intro ============= */}
      <div className="teaser-page page-a">
        <p className="intro-pre">Invitation au mariage</p>
        <div className="monogram">
          <span className="mono-letter mono-y">Y</span>
          <span className="mono-amp">&amp;</span>
          <span className="mono-letter mono-m">M</span>
        </div>
        <span className="mono-rule" />
        <p className="intro-post">12 — 15 AOÛT 2026 · BITAM</p>
      </div>

      {/* ============= Page B — Couple + Pagnes ============= */}
      <div className="teaser-page page-b">
        <div className="reveal reveal-1">
          <p className="invite-pre">Avec la grâce de Dieu</p>
          <h1 className="couple">
            <span className="name-line">Yero Oumar</span>
            <span className="amp shimmer-loop">&amp;</span>
            <span className="name-line">Maïmouna M. Toukour</span>
          </h1>
          <p className="invite-post">célèbrent leur mariage</p>
        </div>

        <div className="reveal reveal-2 pagnes-block">
          <p className="pagnes-pre">Nos Couleurs</p>
          <div className="pagnes-row">
            <figure className="pagne-medallion">
              <div className="medallion-ring">
                <img src="/images/pagne-dandali3.png" alt="Pagne du Dandali" />
              </div>
              <figcaption>Dandali</figcaption>
            </figure>
            <figure className="pagne-medallion">
              <div className="medallion-ring">
                <img src="/images/pagne-yaayi.jpg" alt="Pagne du Yaayi" />
              </div>
              <figcaption>Yaayi</figcaption>
            </figure>
          </div>
        </div>
      </div>

      {/* ============= Page C — Date ============= */}
      <div className="teaser-page page-c">
        <div className="reveal reveal-1">
          <div className="date-banner">
            <p className="date-pre">VOUS INVITENT</p>
            <p className="date-main">
              <span>12 — 15</span>
              <span className="month">AOÛT</span>
              <span>2026</span>
            </p>
            <p className="date-place">BITAM · GABON</p>
          </div>
        </div>
      </div>

      {/* ============= Page D — CTA ============= */}
      <div className="teaser-page page-d">
        <div className="reveal reveal-1">
          <p className="cta-title">Réservez la date</p>
          <p className="cta-sub">Site complet & RSVP en ligne</p>
          <p className="cta-url">yero-et-maimouna.com</p>
        </div>
      </div>

      <style>{`
        .teaser-root {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          background: #F7EEDC;
          overflow: hidden;
          font-family: 'Cormorant Garamond', serif;
        }
        /* contain = full template visible, no cropping of side / top / bottom decorations */
        .teaser-bg {
          position: absolute;
          inset: 0;
          background-image: url('/images/template-mobile-tall.png?v=2');
          background-repeat: no-repeat;
          background-size: cover;
          background-position: center center;
          background-color: #F7EEDC;
          animation: bgZoom 50s ease-in-out infinite;
          z-index: 0;
        }
        @keyframes bgZoom {
          0%, 100% { transform: scale(1.00); }
          50%      { transform: scale(1.04); }
        }
        .teaser-vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 42%, rgba(6,53,36,0.18) 100%);
          pointer-events: none;
          z-index: 0;
        }

        /* ============ Gold sparkle layer (transitions) ============ */
        .sparkles {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          /* "screen" blend makes sparkles add light to the background — pops on the gold filigree */
          mix-blend-mode: screen;
        }
        .sparkle {
          position: absolute;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: radial-gradient(circle, #FFFFFF 0%, #FFF6D5 30%, #FFDD88 55%, rgba(212,175,55,0.4) 75%, transparent 90%);
          box-shadow:
            0 0 14px 3px rgba(255,255,255,1),
            0 0 30px 8px rgba(255,233,168,0.95),
            0 0 56px 16px rgba(255,200,80,0.7);
          opacity: 0;
          will-change: transform, opacity;
        }
        /* Four-pointed star "ray" via pseudo-elements */
        .sparkle::before,
        .sparkle::after {
          content: "";
          position: absolute;
          inset: -16px;
          background: radial-gradient(ellipse 50% 6% at center, #FFFFFF 0%, rgba(255,240,200,0.85) 35%, transparent 70%);
          pointer-events: none;
        }
        .sparkle::after { transform: rotate(90deg); }
        /* Each sparkle peaks at one of 4 transition windows on a 50s loop.
         * Windows: 12.5% (≈6.25s), 41.5% (≈20.75s), 71.5% (≈35.75s), 84.5% (≈42.25s)
         * Spread across screen, staggered slightly for natural shimmer
         */
        .sp-a { top:  18%; left: 22%; animation: sparkBurst 50s ease-out infinite; animation-delay: 0s;    }
        .sp-b { top:  28%; left: 78%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.2s; }
        .sp-c { top:  45%; left: 15%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.4s; }
        .sp-d { top:  60%; left: 82%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.6s; }
        .sp-e { top:  75%; left: 25%; animation: sparkBurst 50s ease-out infinite; animation-delay: 0s;    width: 7px; height: 7px; }
        .sp-f { top:  88%; left: 60%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.3s; width: 7px; height: 7px; }
        .sp-g { top:  12%; left: 55%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.5s; width: 13px; height: 13px; }
        .sp-h { top:  38%; left: 50%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.1s; width: 12px; height: 12px; }
        .sp-i { top:  55%; left: 38%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.7s; }
        .sp-j { top:  68%; left: 68%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.15s; width: 8px; height: 8px; }
        .sp-k { top:  22%; left: 40%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.55s; width: 6px; height: 6px; }
        .sp-l { top:  82%; left: 45%; animation: sparkBurst 50s ease-out infinite; animation-delay: -0.35s; width: 9px; height: 9px; }

        @keyframes sparkBurst {
          /* idle */
          0%, 10.5% { opacity: 0; transform: scale(0.3) rotate(0deg); }
          /* Burst 1: intro → page A around 12.5% (1.5s window) */
          11%       { opacity: 0.95; transform: scale(0.9) rotate(20deg); }
          12.5%     { opacity: 1; transform: scale(1.4) rotate(60deg); }
          14%       { opacity: 0; transform: scale(0.4) rotate(110deg); }

          39.5%     { opacity: 0; transform: scale(0.3) rotate(0deg); }
          /* Burst 2: page A → page B around 41.5% */
          40.5%     { opacity: 0.95; transform: scale(0.9) rotate(20deg); }
          41.5%     { opacity: 1; transform: scale(1.4) rotate(60deg); }
          43%       { opacity: 0; transform: scale(0.4) rotate(110deg); }

          69.5%     { opacity: 0; transform: scale(0.3) rotate(0deg); }
          /* Burst 3: page B → page C around 71.5% */
          70.5%     { opacity: 0.95; transform: scale(0.9) rotate(20deg); }
          71.5%     { opacity: 1; transform: scale(1.4) rotate(60deg); }
          73%       { opacity: 0; transform: scale(0.4) rotate(110deg); }

          82.5%     { opacity: 0; transform: scale(0.3) rotate(0deg); }
          /* Burst 4: page C → page D around 84.5% */
          83.5%     { opacity: 0.95; transform: scale(0.9) rotate(20deg); }
          84.5%     { opacity: 1; transform: scale(1.4) rotate(60deg); }
          86%       { opacity: 0; transform: scale(0.4) rotate(110deg); }

          100%      { opacity: 0; transform: scale(0.3) rotate(0deg); }
        }

        /* ============ Pages ============ */
        .teaser-page {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          /* Content biased slightly upward — leaves room for bottom florals & gives breathing room */
          justify-content: center;
          padding: 11vh 8vw 17vh;
          opacity: 0;
          color: #063524;
          text-align: center;
          z-index: 1;
          gap: clamp(20px, 4vh, 38px);
        }
        .teaser-page > * { max-width: 100%; }

        /* 50s total
         *  Page 0 (versets): 0 → 6.5s        (0% → 13%)
         *  Page A (monogramme): 7 → 21s     (14% → 42%)
         *  Page B (couple): 21.5 → 36s      (43% → 72%)
         *  Page C (date): 36.5 → 42s        (73% → 84%)
         *  Page D (cta): 42.5 → 49.5s       (85% → 99%)
         */
        .page-intro { animation: pageIntro 50s ease-in-out infinite; }
        .page-a     { animation: pageA     50s ease-in-out infinite; }
        .page-b     { animation: pageB     50s ease-in-out infinite; }
        .page-c     { animation: pageC     50s ease-in-out infinite; }
        .page-d     { animation: pageD     50s ease-in-out infinite; }

        @keyframes pageIntro {
          0%, 100% { opacity: 0; }
          1%, 11.5%  { opacity: 1; }
          13%      { opacity: 0; }
        }
        @keyframes pageA {
          0%, 13%, 100% { opacity: 0; }
          14.5%, 41%    { opacity: 1; }
          42%           { opacity: 0; }
        }
        @keyframes pageB {
          0%, 42%, 100% { opacity: 0; }
          43.5%, 71%    { opacity: 1; }
          72%           { opacity: 0; }
        }
        @keyframes pageC {
          0%, 72%, 100% { opacity: 0; }
          73.5%, 83%    { opacity: 1; }
          84%           { opacity: 0; }
        }
        @keyframes pageD {
          0%, 84%, 100% { opacity: 0; }
          85.5%, 99%    { opacity: 1; }
        }

        /* ============ Reveal animations (sequential inside a page) ============ */
        .reveal {
          opacity: 0;
          transform: translateY(14px);
          animation-fill-mode: both;
          animation-timing-function: ease-out;
          animation-duration: 1.1s;
        }
        /* Page 0 (versets) visible from 0s onward; reveals at 0.5, 1.5, 2.5s */
        .page-intro .reveal-1 { animation-name: revealIn; animation-delay: 0.5s; }
        .page-intro .reveal-2 { animation-name: revealIn; animation-delay: 1.5s; }
        .page-intro .reveal-3 { animation-name: revealIn; animation-delay: 2.5s; }
        /* Page A (monogramme) visible 7s onward — animations handled inline */
        .page-a .reveal-1 { animation-name: revealIn; animation-delay: 7.3s; }
        .page-a .reveal-2 { animation-name: revealIn; animation-delay: 11.0s; }
        .page-a .reveal-3 { animation-name: revealIn; animation-delay: 15.5s; }
        /* Page B visible 21.5s onward; reveals at 22, 27.5s */
        .page-b .reveal-1 { animation-name: revealIn; animation-delay: 22.0s; }
        .page-b .reveal-2 { animation-name: revealIn; animation-delay: 27.5s; }
        /* Page C reveal at 37s */
        .page-c .reveal-1 { animation-name: revealIn; animation-delay: 37.0s; }
        /* Page D reveal at 43s */
        .page-d .reveal-1 { animation-name: revealIn; animation-delay: 43.0s; }
        @keyframes revealIn {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        /* ============ Page 0 — Monogramme intro ============ */
        .page-intro { gap: clamp(16px, 3vh, 28px); }
        .intro-pre {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          color: #5a4310;
          font-weight: 700;
          font-size: clamp(15px, 3.8vw, 22px);
          letter-spacing: 0.42em;
          text-transform: uppercase;
          margin: 0;
          text-shadow: 0 0 14px rgba(247,238,220,0.95), 0 1px 0 rgba(247,238,220,0.7);
          opacity: 0;
          animation: introFadeIn 1s ease-out 0.3s forwards;
        }
        .monogram {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: clamp(8px, 2vw, 18px);
          line-height: 1;
        }
        .mono-letter {
          font-family: 'Great Vibes', cursive;
          color: #063524;
          font-size: clamp(110px, 30vw, 200px);
          text-shadow:
            0 0 30px rgba(247,238,220,1),
            0 0 12px rgba(247,238,220,0.95),
            0 0 4px rgba(247,238,220,1);
          filter: drop-shadow(0 4px 10px rgba(6,53,36,0.35));
          opacity: 0;
        }
        .mono-y { animation: monoIn 1.4s cubic-bezier(.22,.61,.36,1) 0.8s forwards; }
        .mono-m { animation: monoIn 1.4s cubic-bezier(.22,.61,.36,1) 1.4s forwards; }
        .mono-amp {
          font-family: 'Great Vibes', cursive;
          font-size: clamp(70px, 18vw, 130px);
          line-height: 1;
          margin: 0 4px;
          color: #145C42;
          text-shadow:
            0 0 28px rgba(247,238,220,1),
            0 0 10px rgba(247,238,220,0.95);
          filter: drop-shadow(0 4px 10px rgba(6,53,36,0.3));
          opacity: 0;
          animation: monoIn 1.4s cubic-bezier(.22,.61,.36,1) 1.1s forwards;
        }
        @keyframes monoIn {
          0%   { opacity: 0; transform: translateY(20px) scale(0.86); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .mono-rule {
          display: block;
          width: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, #C9A961, transparent);
          animation: ruleDraw 1.2s ease-out 2.1s forwards;
        }
        @keyframes ruleDraw {
          to { width: clamp(160px, 50vw, 280px); }
        }
        .intro-post {
          font-family: 'Cormorant Garamond', serif;
          color: #5a4310;
          font-weight: 700;
          font-size: clamp(15px, 3.8vw, 22px);
          letter-spacing: 0.36em;
          margin: 0;
          text-shadow: 0 0 14px rgba(247,238,220,0.95), 0 1px 0 rgba(247,238,220,0.7);
          opacity: 0;
          animation: introFadeIn 1s ease-out 2.4s forwards;
        }
        @keyframes introFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ============ Page A — Versets ============ */
        .bismillah {
          font-family: 'Amiri', serif;
          color: #145C42;
          font-weight: 700;
          font-size: clamp(28px, 7.5vw, 44px);
          letter-spacing: 0.02em;
          white-space: nowrap;
          margin: 0;
        }
        .gold-rule {
          display: block;
          width: 100px;
          height: 1px;
          margin: 14px auto 0;
          background: linear-gradient(90deg, transparent, #C9A961, transparent);
        }
        .verse {
          font-family: 'Amiri', serif;
          color: #063524;
          font-weight: 700;
          font-size: clamp(26px, 7vw, 40px);
          line-height: 2;
          margin: 0;
        }
        .verse-ref {
          font-family: 'Amiri', serif;
          margin-top: 16px;
          color: #5a4310;
          font-weight: 700;
          font-size: clamp(13px, 3vw, 17px);
          letter-spacing: 0.12em;
        }
        .translation {
          font-style: italic;
          font-size: clamp(19px, 4.8vw, 26px);
          line-height: 1.7;
          color: #063524;
          margin: 0;
        }

        /* ============ Page B — Couple + Pagnes ============ */
        .invite-pre {
          font-style: italic;
          font-size: clamp(15px, 3.6vw, 20px);
          color: #5a4310;
          font-weight: 700;
          letter-spacing: 0.4em;
          text-transform: uppercase;
          margin: 0 0 22px;
          text-shadow: 0 0 12px rgba(247,238,220,0.85);
        }
        .invite-post {
          font-style: italic;
          font-size: clamp(14px, 3.5vw, 19px);
          color: #5a4310;
          font-weight: 700;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          margin: 16px 0 0;
          text-shadow: 0 0 12px rgba(247,238,220,0.85);
        }
        .couple {
          font-family: 'Great Vibes', cursive;
          color: #063524;
          font-weight: 400;
          line-height: 1.05;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          margin: 0;
        }
        .name-line {
          display: block;
          font-size: clamp(38px, 9.5vw, 60px);
          text-align: center;
          word-spacing: -0.1em;
          filter: drop-shadow(0 3px 4px rgba(6,53,36,0.20));
        }
        .amp {
          display: block;
          font-family: 'Great Vibes', cursive;
          font-size: clamp(42px, 10.5vw, 66px);
          line-height: 1;
        }
        .shimmer-loop {
          background: linear-gradient(120deg, #C9A961 0%, #F5E5B0 40%, #C9A961 60%, #B8893C 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 3s linear infinite;
        }
        @keyframes shimmer { to { background-position: -200% 0; } }
        @media (max-width: 360px) {
          .name-line { font-size: 32px; }
          .amp { font-size: 36px; }
        }

        .pagnes-block { margin-top: 8px; }
        .pagnes-pre {
          font-family: 'Great Vibes', cursive;
          font-size: clamp(34px, 8.5vw, 48px);
          color: #063524;
          margin: 0 0 18px;
          filter: drop-shadow(0 2px 4px rgba(6,53,36,0.15));
        }
        .pagnes-row {
          display: flex;
          gap: clamp(18px, 5vw, 36px);
          justify-content: center;
          align-items: flex-start;
        }
        .pagne-medallion {
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .medallion-ring {
          width: clamp(120px, 32vw, 170px);
          height: clamp(120px, 32vw, 170px);
          border-radius: 50%;
          padding: 6px;
          background: radial-gradient(circle at 30% 30%, #F3D97A 0%, #C9A961 45%, #5a4310 100%);
          box-shadow:
            0 18px 36px -16px rgba(10,61,46,0.6),
            inset 0 0 0 2px rgba(255,255,255,0.4);
          animation: medallionFloat 5s ease-in-out infinite;
        }
        .pagne-medallion:nth-child(2) .medallion-ring { animation-delay: 0.6s; }
        .medallion-ring img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          border: 2px solid #F7EEDC;
        }
        @keyframes medallionFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-7px) rotate(0.6deg); }
        }
        .pagne-medallion figcaption {
          margin-top: 12px;
          font-family: 'Great Vibes', cursive;
          color: #063524;
          font-size: clamp(24px, 6vw, 34px);
          line-height: 1;
          filter: drop-shadow(0 2px 3px rgba(6,53,36,0.18));
        }

        /* ============ Page C — Date banner ============ */
        .date-banner {
          background: linear-gradient(180deg, #145C42 0%, #0a3d2e 100%);
          border: 2px solid #C9A961;
          box-shadow:
            inset 0 0 0 4px rgba(10,61,46,1),
            inset 0 0 0 5px #C9A961,
            0 22px 55px -20px rgba(10,61,46,0.7);
          padding: clamp(30px, 6vw, 46px) clamp(34px, 8vw, 60px);
          color: #F5E5B0;
        }
        .date-pre {
          font-size: clamp(13px, 3vw, 17px);
          letter-spacing: 0.45em;
          color: #9D6E39;
          margin: 0 0 20px;
        }
        .date-main {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(46px, 12vw, 72px);
          font-weight: 300;
          line-height: 1.05;
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin: 0;
          color: #9D6E39;
        }
        .month { color: #9D6E39; font-size: 0.78em; letter-spacing: 0.16em; }
        .date-place {
          font-size: clamp(14px, 3.2vw, 18px);
          letter-spacing: 0.4em;
          color: #9D6E39;
          margin: 20px 0 0;
        }

        /* ============ Page D — CTA ============ */
        .cta-title {
          font-family: 'Great Vibes', cursive;
          font-size: clamp(64px, 15vw, 100px);
          color: #063524;
          line-height: 1;
          margin: 0;
          filter: drop-shadow(0 3px 6px rgba(6,53,36,0.2));
        }
        .cta-sub {
          font-style: italic;
          font-size: clamp(19px, 4.5vw, 28px);
          color: #063524;
          margin: 22px 0 0;
        }
        .cta-url {
          margin: 30px 0 0;
          font-size: clamp(15px, 3.5vw, 22px);
          letter-spacing: 0.3em;
          color: #0a3d2e;
          padding: 16px 32px;
          border: 1px solid #C9A961;
          border-radius: 999px;
          background: rgba(255,253,247,0.95);
          box-shadow: 0 10px 28px -12px rgba(10,61,46,0.4);
          display: inline-block;
        }

        /* Letterbox 9:16 on landscape desktop */
        @media (min-aspect-ratio: 10/16) {
          .teaser-root {
            max-width: calc(100vh * 9 / 16);
            margin: 0 auto;
            position: relative;
            box-shadow: 0 0 80px rgba(0,0,0,0.45);
          }
          body { background: #0a0a0a; }
        }
      `}</style>
    </div>
  );
}
